import React, { useEffect, useState } from 'react';
import { fade, makeStyles } from '@material-ui/core/styles';
import { FormControl, InputLabel, Select, MenuItem, TextField, OutlinedInput } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles(theme => {
    const white = theme.palette.common.white;

    return {
        headerFilter: {
            color: white
        },
        inputRoot: {
            color: fade(white, 0.86),
            backgroundColor: fade(white, 0.08),
            borderRadius: 8,
            '&:hover $notchedOutline': {
                borderColor: fade(white, 0.6)
            },
            '&$inputFocused': {
                color: fade(white, 0.92),
                backgroundColor: fade(theme.palette.common.white, 0.12)
            },
            '&$inputFocused $notchedOutline': {
                borderColor: fade(white, 0.72),
                borderWidth: 1
            }
        },
        inputFocused: {},
        inputInput: {
            color: fade(white, 0.86),
            opacity: 1,
            WebkitTextFillColor: fade(white, 0.86),
            '&::placeholder': {
                color: fade(white, 0.58),
                opacity: 1
            }
        },
        notchedOutline: {
            borderColor: fade(white, 0.45),
            borderRadius: 8,
            opacity: 1,
            '& legend, & legend span': {
                color: fade(white, 0.86),
                WebkitTextFillColor: fade(white, 0.86)
            },
            '& legend[class*="PrivateNotchedOutline-legendLabelled"], & legend[class*="PrivateNotchedOutline-legendLabelled"] span': {
                color: fade(white, 0.86),
                WebkitTextFillColor: fade(white, 0.86)
            }
        },
        inputLabelRoot: {
            color: fade(white, 0.68),
            opacity: 1,
            WebkitTextFillColor: fade(white, 0.68),
            '&$inputLabelFocused': {
                color: fade(white, 0.86),
                WebkitTextFillColor: fade(white, 0.86)
            }
        },
        inputLabelFocused: {},
        inputLabelOutlined: {
            color: fade(white, 0.68),
            WebkitTextFillColor: fade(white, 0.68),
            '&$inputLabelShrink': {
                color: fade(white, 0.86),
                WebkitTextFillColor: fade(white, 0.86)
            }
        },
        inputLabelShrink: {},
        selectIcon: {
            color: fade(white, 0.68)
        },
        selectSelect: {
            '&:focus': {
                backgroundColor: 'transparent'
            }
        }
    };
});

export default function TableBrowserHeaderFilters({ config, filterList, filterSelection, dispatch }) {
    const classes = useStyles();
    const { t } = useTranslation(config.topic ? ['tableBrowser', 'common', config.topic] : ['tableBrowser', 'common']);
    const inputLabelClasses = {
        root: classes.inputLabelRoot,
        focused: classes.inputLabelFocused,
        outlined: classes.inputLabelOutlined,
        shrink: classes.inputLabelShrink
    };
    const outlinedInputClasses = {
        root: classes.inputRoot,
        focused: classes.inputFocused,
        input: classes.inputInput,
        notchedOutline: classes.notchedOutline
    };
    const selectClasses = {
        icon: classes.selectIcon,
        select: classes.selectSelect
    };

    if (!config.filterFields) return null;

    const visibleFields = config.filterFields.filter(f => f.showInHeader);

    if (visibleFields.length === 0) return null;

    const getFieldValue = field => {
        const rawValue = filterList.find(f => f.propertyName === field.propertyName)?.value;

        if (field.type === 'IS_NULL')
            return rawValue == null ? '' : rawValue.toString();

        return rawValue ?? '';
    };

    const handleChange = (field, selected) => {
        const newFilterList = filterList.filter(f => f.propertyName !== field.propertyName);

        if (field.type === 'IS_NULL') {
            if (selected !== '')
                newFilterList.push({ propertyName: field.propertyName, type: field.type, value: selected === 'true' });
        } else if (selected !== '') {
            newFilterList.push({ propertyName: field.propertyName, type: field.type, value: selected });
        }

        dispatch({ type: `${config.key}/filter/setFilterList`, payload: newFilterList });
    };

    return (
        <>
            {visibleFields.map(field => {
                const value = getFieldValue(field);
                const isTextField = field.propertyType === 'String' && field.type === 'LIKE';
                const isNullField = field.type === 'IS_NULL';

                const items = isNullField
                    ? [{ value: 'true', label: t('No') }, { value: 'false', label: t('Yes') }]
                    : field.propertyType === 'Long' ? filterSelection?.entityMap?.[field.propertyName] || [] : filterSelection?.enumMap?.[field.propertyName]?.items || [];

                const label = field.text;

                if (isTextField) {
                    return (
                        <TableBrowserHeaderTextFilter
                            key={field.propertyName}
                            field={field}
                            label={label}
                            value={value}
                            className={'ml-8 ' + classes.headerFilter}
                            inputLabelClasses={inputLabelClasses}
                            outlinedInputClasses={outlinedInputClasses}
                            onChange={handleChange}
                        />
                    );
                }

                return (
                    <FormControl key={field.propertyName} variant='outlined' size='small' className={'ml-8 ' + classes.headerFilter}>
                        <InputLabel classes={inputLabelClasses}>{label}</InputLabel>
                        <Select
                            label={label}
                            value={value}
                            onChange={e => handleChange(field, e.target.value)}
                            input={<OutlinedInput label={label} classes={outlinedInputClasses} />}
                            classes={selectClasses}
                            style={{ minWidth: 120 }}
                        >
                            <MenuItem value=''>{t('All')}</MenuItem>
                            {isNullField
                                ? items.map(item => (
                                    <MenuItem key={item.value} value={item.value}>
                                        {item.label}
                                    </MenuItem>
                                ))
                                : items.map(item => (
                                    <MenuItem key={item.id ?? item.value} value={item.id ?? item.value}>
                                        {item.name ?? item.label}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                );
            })}
        </>
    );
}

function TableBrowserHeaderTextFilter({ field, label, value, className, inputLabelClasses, outlinedInputClasses, onChange }) {
    const filterValue = value ?? '';
    const [text, setText] = useState(filterValue);

    useEffect(() => {
        setText(filterValue);
    }, [filterValue]);

    useEffect(() => {
        if (text === filterValue) return undefined;

        const timeOutId = setTimeout(() => {
            onChange(field, text);
        }, 500);

        return () => clearTimeout(timeOutId);
    }, [field, filterValue, onChange, text]);

    return (
        <TextField
            variant='outlined'
            size='small'
            label={label}
            value={text}
            onChange={e => setText(e.target.value)}
            className={className}
            InputLabelProps={{ classes: inputLabelClasses }}
            InputProps={{ classes: outlinedInputClasses }}
            style={{ minWidth: 160 }}
        />
    );
}
